/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from "react";
import "./styles.css";
import DataTable from "react-data-table-component";
import { CloseIcon, InstagramIcon, TwitterIcon } from "src/img/svg";
import whiteLogo from "src/img/logo-white.png";
import * as Yup from "yup";
import { Formik, Form, FieldArray, ErrorMessage } from "formik";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAllAppliances, submitInquires } from "src/services/api";
import { renderAlert, renderToastMsg } from "src/utils/functions";

function roundToTwoDecimalPlaces(num) {
	return Number(num.toFixed(2));
}

const Calculator = () => {
	const [menuActive, setMenuActive] = useState(false);
	const toggleMenu = () => setMenuActive(!menuActive);

	const { data: applianceData } = useQuery({
		queryKey: ["appliance"],
		queryFn: () => getAllAppliances({ status: "enabled", no_paginate: true }),
		suspense: true,
		retry: 1,
	});

	const appliancesList = applianceData?.data || [];

	const initialValues = {
		name: "",
		phone: "",
		email: "",
		appliances: [
			{
				appliance: {},
				quantity: 1,
				usage: 1,
				energy_usage: "day-and-night",
				priority: 0
			},
		],
	};

	const validationSchema = Yup.object().shape({
		name: Yup.string()
			.trim()
			.required("Full name is required")
			.min(3, "Name must be at least 3 characters long"),
		phone: Yup.string()
			.required("Phone number is required")
			.matches(/^\+?\d{10,15}$/, "Enter a valid phone number"),
		email: Yup.string()
			.email("Enter a valid email address")
			.required("Email is required"),
		appliances: Yup.array()
			.of(
				Yup.object().shape({
					appliance: Yup.object()
						.required("Appliance is required")
						.test("not-empty", "Appliance is required", (value) => !!value?.id),
					quantity: Yup.number()
						.required("Quantity is required")
						.min(1, "Must be at least 1"),
					usage: Yup.number()
						.required("Usage is required")
						.min(0.1, "Must be greater than 0"),
					energy_usage: Yup.string()
						.required("Energy usage is required"),
					priority: Yup.number(),
				})
			)
			.min(1, "At least one appliance is required"),
	});

	const { mutate, isLoading } = useMutation(submitInquires);


	const handleSubmit = (values, { resetForm }) => {
		try {
			const minRating = values.appliances.reduce(
				(sum, item) => sum + (parseFloat(item?.appliance?.minRating) || 0) * (item.quantity || 0),
				0
			);
			const maxRating = values.appliances.reduce(
				(sum, item) => sum + (parseFloat(item?.appliance?.maxRating) || 0) * (item.quantity || 0),
				0
			);

			const postData = {
				email: values.email,
				fullName: values.name,
				phoneNumber: values.phone,
				avgUsage: `${roundToTwoDecimalPlaces(minRating)}-${roundToTwoDecimalPlaces(maxRating)}`,
				appliances: values.appliances.map((item) => ({
					appliance_id: item?.appliance?.id,
					qty: item.quantity,
					avg_daily_usage: item.usage,
					priority: item.priority,
					energy_usage: item.energy_usage,
				})),
			};

			mutate(postData, {
				onSuccess: (res) => {
					if (res?.status === "success") {
						// resetForm();
						renderAlert({
							title: "Success",
							msg: res?.msg,
							type: "success",
						})
					} else {
						renderToastMsg(res?.msg);
					}
				},
			});
		} catch (error) {
			renderToastMsg("Failed to submit form. Please try again.");
		}
	};

	return (
		<>
			<HeaderComponent toggleMenu={toggleMenu} menuActive={menuActive} />

			<div className="container3">
				<div className="calculator-container">
					<h2 className="calculator-heading">Energy Calculator</h2>
					<p className="calculator-subheading">
						This determines the instantaneous energy, sustained energy, and power consumed by the electrical appliances in your home,
						given two of the electrical parameters (voltage, current, or resistance).
					</p>
				</div>

				<div style={{ marginBottom: 40 }}>
					<Formik
						initialValues={initialValues}
						validationSchema={validationSchema}
						onSubmit={handleSubmit}
					>
						{({ values, setFieldValue, errors, touched }) => {
							const selectedIds = values.appliances
								.map((item) => item.appliance?.id)
								.filter((id) => !!id);

							// Calculate total power rating
							const totalMinRating = values.appliances.reduce(
								(sum, item) => sum + (parseFloat(item?.appliance?.minRating) || 0) * (item.quantity || 0),
								0
							);
							const totalMaxRating = values.appliances.reduce(
								(sum, item) => sum + (parseFloat(item?.appliance?.maxRating) || 0) * (item.quantity || 0),
								0
							);

							return (
								<Form>
									<div className="calculator-form">
										<div className="form-group" style={{ marginBottom: 30 }}>
											<label style={{ marginBottom: 20 }}>
												Enter your name, phone number and email address:
											</label>
											<input
												type="text"
												name="name"
												className="form-control"
												placeholder="Full Name"
												style={{ fontSize: 14 }}
												onChange={(e) => setFieldValue("name", e.target.value)}
												value={values.name}
											/>
											<ErrorMessage
												name="name"
												component="div"
												style={{ fontSize: 12, color: "red", marginTop: 4 }}
											/>
										</div>

										<div className="form-group" style={{ marginBottom: 30 }}>
											<input
												type="text"
												name="phone"
												className="form-control"
												placeholder="Phone Number"
												style={{ fontSize: 14 }}
												onChange={(e) => setFieldValue("phone", e.target.value)}
												value={values.phone}
											/>
											<ErrorMessage
												name="phone"
												component="div"
												style={{ fontSize: 12, color: "red", marginTop: 4 }}
											/>
										</div>

										<div className="form-group" style={{ marginBottom: 40 }}>
											<input
												type="email"
												name="email"
												className="form-control"
												placeholder="Email Address"
												style={{ fontSize: 14 }}
												onChange={(e) => setFieldValue("email", e.target.value)}
												value={values.email}
											/>
											<ErrorMessage
												name="email"
												component="div"
												style={{ fontSize: 12, color: "red", marginTop: 4 }}
											/>
										</div>
									</div>

									<div className="form-group">
										<label>Use this to calculate your appliance energy</label>
									</div>

									<FieldArray
										name="appliances"
										render={(arrayHelpers) => {
											window.applianceArrayHelpers = arrayHelpers;

											return (
												<>
													<div style={{ overflowX: 'auto', marginBottom: '2rem' }}>
														<DataTable
															columns={[

																{
																	name: "Appliance",
																	cell: (row, index) => (
																		<div style={{ minWidth: '150px' }}>
																			<select
																				className="form-control custom-form-control"
																				value={row.appliance?.id || ""}
																				onChange={(e) => {
																					const selectedApp = appliancesList.find(
																						(a) => a.id === e.target.value
																					);
																					setFieldValue(`appliances.${index}.appliance`, selectedApp || {});
																				}}
																			>
																				<option value="">Select Appliance</option>
																				{appliancesList.map((app, i) => (
																					<option
																						key={i}
																						value={app.id}
																						disabled={selectedIds.includes(app.id) && app.id !== row.appliance?.id}
																					>
																						{app.name}
																					</option>
																				))}
																			</select>
																			{touched.appliances?.[index]?.appliance && errors.appliances?.[index]?.appliance && (
																				<div style={{ fontSize: 12, color: "red", marginTop: 4 }}>
																					{errors.appliances[index].appliance}
																				</div>
																			)}
																		</div>
																	),
																	grow: 2,
																	minWidth: '200px',
																},
																{
																	name: "Power Rating (Watt(s))",
																	cell: (row) =>
																		row.appliance?.minRating
																			? `${row.appliance?.minRating}-${row.appliance?.maxRating}`
																			: "-",
																	minWidth: '150px',
																},
																{
																	name: "Time of Usage",
																	cell: (row, index) => (
																		<div style={{ minWidth: '140px' }}>
																			<select
																				className="form-control custom-form-control"
																				value={row.energy_usage}
																				onChange={(e) =>
																					setFieldValue(`appliances.${index}.energy_usage`, e.target.value)
																				}
																			>
																				<option value="day-and-night">Day and night</option>
																				<option value="day">Day</option>
																				<option value="night">Night</option>
																			</select>
																		</div>
																	),
																	minWidth: '160px',
																},
																{
																	name: "Quantity",
																	cell: (row, index) => (
																		<div style={{ minWidth: '80px' }}>
																			<input
																				type="number"
																				className="form-control custom-form-control"
																				value={row.quantity}
																				min={1}
																				onChange={(e) =>
																					setFieldValue(`appliances.${index}.quantity`, +e.target.value)
																				}
																			/>
																			{touched.appliances?.[index]?.quantity && errors.appliances?.[index]?.quantity && (
																				<div style={{ fontSize: 11, color: "red", marginTop: 2 }}>
																					{errors.appliances[index].quantity}
																				</div>
																			)}
																		</div>
																	),
																	grow: 0.5,
																	minWidth: '100px',
																},
																{
																	name: "Average Daily Usage (hours)",
																	cell: (row, index) => (
																		<div style={{ minWidth: '100px' }}>
																			<input
																				type="number"
																				step={0.1}
																				min={0}
																				className="form-control custom-form-control"
																				value={row.usage}
																				onChange={(e) =>
																					setFieldValue(`appliances.${index}.usage`, +e.target.value)
																				}
																			/>
																			{touched.appliances?.[index]?.usage && errors.appliances?.[index]?.usage && (
																				<div style={{ fontSize: 11, color: "red", marginTop: 2 }}>
																					{errors.appliances[index].usage}
																				</div>
																			)}
																		</div>
																	),
																	grow: 1.5,
																	minWidth: '180px',
																},
																{
																	name: "Necessity",
																	cell: (row, index) => (
																		<input
																			type="checkbox"
																			style={{ accentColor: "#f40000" }}
																			checked={!!row.priority}
																			onChange={(e) =>
																				setFieldValue(
																					`appliances.${index}.priority`,
																					e.target.checked ? 1 : 0
																				)
																			}
																		/>
																	),
																	grow: 0.5,
																	minWidth: '100px',
																},
																{
																	name: "Action",
																	cell: (_, index) =>
																		values.appliances.length === 1 ? (
																			<span style={{ color: "#999", fontSize: 13 }}>Required</span>
																		) : (
																			<button
																				type="button"
																				className="remove-btn"
																				onClick={() => arrayHelpers.remove(index)}
																			>
																				<CloseIcon /> Remove
																			</button>
																		),
																	grow: 0.6,
																	minWidth: '160px',
																},
															]}
															data={values.appliances}
															customStyles={{
																table: {
																	style: {
																		border: "1px solid #EAEBF0",
																		borderRadius: 8,
																		minWidth: '1200px',
																	}
																},
																rows: {
																	style: {
																		fontSize: "14px",
																		borderBottom: "1px solid #EAEBF0",
																		paddingBlock: 20
																	}
																},
																headRow: {
																	style: {
																		fontSize: "14px",
																		fontWeight: 500,
																		backgroundColor: "#F6F6F6"
																	}
																},
															}}
														/>
													</div>

													{typeof errors.appliances === 'string' && (
														<div style={{ color: "red", fontSize: 12, marginTop: 4, marginBottom: 20, textAlign: 'center' }}>
															{errors.appliances}
														</div>
													)}
												</>
											);
										}}
									/>

									{/* Total Power Rating Display */}
									<div className="text-center mb-8 text-[4rem] font-semibold">
										{roundToTwoDecimalPlaces(totalMinRating)}Watt(s) - {roundToTwoDecimalPlaces(totalMaxRating)}Watt(s)
									</div>

									{/* Add Another Appliance and Submit Buttons */}
									<div className="flex justify-center gap-4 flex-wrap">
										<button
											type="button"
											className="btn contact-us-btn nav-link"
											disabled={isLoading}
											onClick={() => {
												if (window.applianceArrayHelpers) {
													window.applianceArrayHelpers.push({
														appliance: {},
														quantity: 1,
														usage: 1,
														energy_usage: "day-and-night",
														priority: 0,
													});
												}
											}}
										>
											Add Another Appliance
										</button>

										<button
											type="submit"
											className="get-started btn-loader-red m-0"
											disabled={isLoading}
										>
											Submit
										</button>
									</div>
								</Form>
							);
						}}
					</Formik>
				</div>
			</div>

			<FooterComponent />
		</>
	);
};

const FooterComponent = () => (
	<section>
		<footer className="">
			<div className="container">
				<div className="footer-ins">
					<div className="footer-itm footer-abt">
						<div className="footer-logo">
							<img src={whiteLogo} alt="revocube-energies" />
						</div>
						<p>
							Get solar installed today with down payment. Pay a reasonable monthly fee and enjoy an uninterrupted power supply.
						</p>
					</div>
					<div className="footer-itm">
						<h5>Address</h5>
						<p>
							12 Ajisafe street, GRA Ikeja, <br /> Lagos, Nigeria
						</p>
					</div>
					<div className="footer-itm">
						<h5>Contact</h5>
						<p>info@revocubeenergies.com</p>
						<p>+1 888 705 0968</p>
						<p>+234 705 394 4594</p>
						<p>+234 1 291 4298</p>
					</div>
					{/* <div className="footer-itm">
						<h5>Get Email Notifications</h5>
						<p>Sign up to newsletter to receive news & updates.</p>
					</div> */}
				</div>
				<div>
					<div className="footer-sub-container">
						<p className="footer-link123">
							© {new Date().getFullYear()} Revocube Energies. All rights Reserved.
						</p>
						<div className="footer-normal">
							<a href=" https://www.instagram.com/revocubeenergies" target="_blank" rel="noreferrer">
								<InstagramIcon />
							</a>
							<a href="https://twitter.com/revocubenergies" target="_blank" rel="noreferrer">
								<TwitterIcon />
							</a>
						</div>
					</div>
				</div>
			</div>
		</footer>
	</section>
);

const HeaderComponent = ({ toggleMenu, menuActive }) => (
	<header className={`header_wrapper ${menuActive ? "active" : ""}`}>
		<div className="header_ins">
			<div className="logo">
				<a href="https://revocubeenergies.com">
					<div className={`logo_ins ${menuActive ? "dark" : "normal"}`}></div>
				</a>
			</div>

			<div className={`main_navigation ${menuActive ? "active" : ""}`}>
				<ul>
					<li><a href="https://www.revocubeenergies.com/about">About</a></li>
					<li><a href="https://www.revocubeenergies.com/solutions/schools">Schools</a></li>
					<li><a href="https://www.revocubeenergies.com/solutions/business">Business</a></li>
					<li><a href="https://www.revocubeenergies.com/solutions/residential">Residences</a></li>
					<li><a href="https://revocubeenergies.com/contact">Contact us</a></li>
				</ul>
				<div className="extra_nav-btn">
					<a href="https://revocubeenergies.com/energy-calculator">Energy calculator</a>
				</div>
			</div>

			<div className={`hamburger ${menuActive ? "active" : ""}`} id="toggle_menu" onClick={toggleMenu}>
				<span></span><span></span><span></span>
			</div>
		</div>
	</header>
);

export default Calculator;